export const MIN_PASSWORD_LENGTH = 15;
export const MAX_PASSWORD_BYTES = 72;

const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
const DANGEROUS_PATH_ENCODING = /%(?:0a|0d|5c)/i;

/**
 * Keep post-auth navigation same-origin. Next's client router must never
 * receive an untrusted absolute/protocol-relative URL.
 */
export function safeNextPath(value: string | null | undefined, fallback = "/"): string {
  if (
    typeof value !== "string" || !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    CONTROL_CHARACTERS.test(value) ||
    DANGEROUS_PATH_ENCODING.test(value)
  ) {
    return fallback;
  }

  try {
    const base = new URL("https://pauta.invalid");
    const parsed = new URL(value, base);
    if (parsed.origin !== base.origin) return fallback;
    // Dot-segment normalization can turn /a/..//host into //host. Validate
    // the normalized path too, including bounded router decoding layers, so
    // returning a relative string cannot change its origin when used again.
    let pathname = parsed.pathname;
    let settled = false;
    for (let pass = 0; pass < 5; pass++) {
      const normalized = new URL(pathname, base);
      if (normalized.origin !== base.origin || normalized.pathname.startsWith("//") ||
        pathname.includes("\\") || CONTROL_CHARACTERS.test(pathname)) return fallback;
      const decoded = decodeURIComponent(pathname);
      if (decoded === pathname) { settled = true; break; }
      pathname = decoded;
    }
    if (!settled) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

/** Keep the UI aligned with the hosted Supabase password policy. */
export function passwordPolicyError(password: string): string | null {
  // Count Unicode code points rather than UTF-16 code units. People can use
  // spaces and any script in a memorable passphrase without an emoji being
  // counted as two characters.
  const length = Array.from(password).length;
  if (length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  // GoTrue/bcrypt caps passwords at 72 UTF-8 bytes. Enforce the exact backend
  // boundary in both browser and server code so Unicode passphrases do not
  // pass the UI and then fail during Auth Admin setup.
  if (new TextEncoder().encode(password).byteLength > MAX_PASSWORD_BYTES) {
    return `Password must be ${MAX_PASSWORD_BYTES} UTF-8 bytes or fewer.`;
  }
  return null;
}

export function passwordPolicyHint(): string {
  return `${MIN_PASSWORD_LENGTH}+ characters. A memorable passphrase works well.`;
}

export function signInErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  if (message.includes("email not confirmed")) {
    return "Confirm your email before signing in. Check your inbox for the Pauta confirmation email.";
  }
  if (message.includes("weak password")) {
    return "This password no longer meets Pauta's security policy. Reset it to continue.";
  }
  if (message.includes("rate limit") || message.includes("too many")) {
    return "Too many attempts. Wait a few minutes, then try again.";
  }
  return "Email or password is incorrect.";
}

export function accountCreationErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  const code =
    error && typeof error === "object" && "code" in error
      ? String(error.code).toLowerCase()
      : "";
  if (
    code === "weak_password" ||
    message.includes("weak password") ||
    message.includes("pwned") ||
    message.includes("breach")
  ) {
    return "Choose a different passphrase within the length limit that has not appeared in a known data breach.";
  }
  if (message.includes("rate limit") || message.includes("too many")) {
    return "Too many attempts. Wait a few minutes, then try again.";
  }
  return "We couldn't create the account. Check the details and try again.";
}
